import type { Locale } from "@i18n/react";

import { HEADERS } from "../../constants/headers/headers.constants";
import { HEADERS_AUTHENTICATED } from "../../constants/headers/headersAuthenticated.constants";
import { HEADERS_OTHER } from "../../constants/headers/headersOther.constants";
import { HEADERS_SEARCH } from "../../constants/headers/headersSearch.constants";
import { HEADERS_TRUSTED } from "../../constants/headers/headersTrusted.constants";
import { getCookie } from "../../lib/cookies/utils/getCookie";
import { getSessionRequestHeaders } from "../../session/utils/getSessionRequestHeaders";
import { determineClientOrServer } from "../../utils/determineClientOrServer";
import { getI18nRequestHeaders } from "../../utils/getI18nRequestHeaders";
import type { RequestType } from "../types/enums/requestType.enum";

export type GetRequestHeadersProps = {
  requestType: RequestType;
  values?: {
    appSession?: string;
    locale?: Locale;
    token?: string;
  };
};

export const getRequestHeaders = async ({
  values,
  requestType,
}: GetRequestHeadersProps): Promise<HeadersInit> => {
  let headers: HeadersInit = HEADERS;

  if (requestType === "other") return HEADERS_OTHER;

  if (values?.appSession)
    headers = {
      ...headers,
      "X-Hallow-App-Session": values?.appSession,
    };

  headers = {
    ...headers,
    ...(await getI18nRequestHeaders({ valueLocale: values?.locale })),
  };

  if (requestType === "authenticated")
    headers = {
      ...headers,
      ...(await getSessionRequestHeaders({ valueToken: values?.token })),
    };

  if (determineClientOrServer() === "client") {
    if (window.location.search) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("isAndroid") === "true")
        headers = { ...headers, "X-Cross-Platform-Origin": "Android" };
      else if (urlParams.get("isIos") === "true")
        headers = { ...headers, "X-Cross-Platform-Origin": "iOS" };
    }

    headers = {
      ...headers,
      "X-CID": getCookie({ key: "_fbc" }),
      "X-PID": getCookie({ key: "_fbp" }),
      "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone,
    };
  }

  switch (requestType) {
    case "authenticated":
      return { ...headers, ...HEADERS_AUTHENTICATED };
    case "search":
      return { ...headers, ...HEADERS_SEARCH };
    case "trusted":
      return { ...headers, ...HEADERS_TRUSTED };
    case "default":
    default:
      return headers;
  }
};
